import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IoSend, IoSearch, IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { getMyThreads, getThreadMessages, sendMessage, getOrCreateTenantAdminThread, getOrCreateLandlordAdminThread, getOrCreateBnbHostAdminThread, markThreadAsRead, getThreadDisplayLabel } from '../../../api/services/chatService';
import { getProfile, getAuthToken } from '../../../api/services/authService';
import { Client } from '@stomp/stompjs';
import API_BASE_URL from '../../../api/config/apiConfig';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './Messages.css';

const Messages = ({ userRole, threadId, threadData }) => {
  const location = useLocation();
  const [activeThread, setActiveThread] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
  const [currentUserId, setCurrentUserId] = useState(null);

  const messagesEndRef = useRef(null);
  const appliedNavThreadIdRef = useRef(null);

  useEffect(() => {
    fetchThreads();
    fetchCurrentUser();

    // Optional: Polling for new threads every 30 seconds
    const interval = setInterval(fetchThreads, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSockets / Realtime
  useEffect(() => {
    if (!activeThread?.id) return;

    const token = getAuthToken();
    const client = new Client({
      // We are dropping SockJS entirely to bypass the /info 401 issue
      // We use standard WebSocket secure protocol (wss://)
      brokerURL: `${API_BASE_URL.replace('http', 'ws')}/ws-native?token=${token}`,
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      debug: (str) => {
        // console.log('STOMP: ' + str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = (frame) => {
      console.log('Connected to STOMP');
      client.subscribe(`/topic/chat/threads/${activeThread.id}`, (message) => {
        if (message.body) {
          try {
            const eventData = JSON.parse(message.body);
            console.log('Received chat event:', eventData);

            if (eventData.event === 'MESSAGE_CREATED') {
              const incomingMsg = eventData.message || eventData.payload || eventData.data;
              
              if (incomingMsg && incomingMsg.id) {
                // If backend provided the raw message, append it directly for instant display
                setActiveThread(prev => {
                  if (prev?.id === activeThread.id) {
                    // Make sure we don't duplicate our own optimistic message
                    const msgExists = (prev.messages || []).some(m => String(m.id) === String(incomingMsg.id));
                    if (!msgExists) {
                      const normalizedMsg = {
                        ...incomingMsg,
                        text: incomingMsg.text || incomingMsg.body || incomingMsg.content || '',
                        time: new Date(incomingMsg.createdAt || incomingMsg.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      };
                      
                      // Also instantly update the sidebar preview!
                      setThreads(prevThreads => (prevThreads || []).map(t => 
                        String(t.id) === String(prev?.id)
                          ? { ...t, displayMsg: normalizedMsg.text, displayTime: normalizedMsg.time }
                          : t
                      ));

                      return { ...prev, messages: [...(prev.messages || []), normalizedMsg] };
                    }
                  }
                  return prev;
                });
              } else {
                // If backend only sent an event ping, wait a bit for the Spring Boot DB transaction to fully commit
                // before querying the API! This fixes the "always one message behind" glitch.
                setTimeout(() => {
                  refreshActiveThreadMessages(activeThread.id);
                }, 400);
              }
            } else if (eventData.event === 'THREAD_READ') {
              // Optionally refresh thread list or unread counts
              fetchThreads();
            }
          } catch (e) {
            console.error('Failed to parse STOMP message', e);
          }
        }
      });
    };

    client.onStompError = (frame) => {
      console.error('Broker reported error: ' + frame.headers['message']);
      console.error('Additional details: ' + frame.body);
    };

    client.activate();

    return () => {
      if (client.active) {
        client.deactivate();
        console.log('STOMP deactivated');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.id]);

  const refreshActiveThreadMessages = async (threadId) => {
    try {
      const res = await getThreadMessages(threadId, 0, 30);
      const rawMessages = Array.isArray(res) ? res : (res?.content || res?.data || []);
      const messages = [...rawMessages].reverse(); // Latest at bottom

      let finalMessages = messages;

      setActiveThread(prev => {
        if (prev?.id === threadId) {
          const dbMessageIds = new Set(messages.map(m => String(m.id)));
          const optimisticMessagesToKeep = (prev.messages || []).filter(m => {
            return m.isOptimistic && !dbMessageIds.has(String(m.id));
          });
          finalMessages = [...messages, ...optimisticMessagesToKeep];
          return { ...prev, messages: finalMessages };
        }
        return prev;
      });

      // Guarantee the left sidebar matches the exact database truth
      if (finalMessages.length > 0) {
        const latestMsg = finalMessages[finalMessages.length - 1]; // Because we did reverse() above
        setThreads(prev => (prev || []).map(t => 
          String(t.id) === String(threadId)
            ? { 
                ...t, 
                displayMsg: latestMsg.text || latestMsg.body || latestMsg.content || '', 
                displayTime: latestMsg.time || (latestMsg.timestamp ? new Date(latestMsg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : t.displayTime)
              }
            : t
        ));
      }
    } catch (err) {
      console.error('Failed to refresh messages via socket event:', err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await getProfile();
      const userData = res.data || res;
      setCurrentUserId(userData.id || userData.userId);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  useEffect(() => {
    if (activeThread?.messages) {
      scrollToBottom();
    }
  }, [activeThread?.messages]);

  useEffect(() => {
    const handleThreadOpened = (e) => {
      const openedThread = e.detail;
      if (!openedThread?.id) return;

      setThreads((prev) => {
        const existing = prev?.find((t) => String(t.id) === String(openedThread.id));
        if (existing) {
          return prev;
        }
        return [openedThread, ...(prev || [])];
      });
    };

    window.addEventListener('app:thread_opened', handleThreadOpened);
    return () => window.removeEventListener('app:thread_opened', handleThreadOpened);
  }, []);

  // Handle browser back button for mobile
  useEffect(() => {
    const handlePopState = () => {
      if (window.innerWidth <= 768) {
        setMobileView('list');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Listen for FCM foreground notifications to trigger instant refresh,
  // fixing issues where STOMP events don't fire or fetch too early before DB commit.
  useEffect(() => {
    const handleNewNotification = (e) => {
      const payload = e.detail;
      const data = payload?.data || {};
      const notifyThreadId = data.thread_id || data.threadId || data.targetId || data.target_id || payload.notification?.tag;
      
      // 1. Refresh thread list on sidebar
      fetchThreads();

      // 2. If we are currently viewing the thread that received the message, append it or refresh
      if (activeThread?.id && (!notifyThreadId || String(notifyThreadId) === String(activeThread.id))) {
         setTimeout(() => { // wait for DB to fully commit before querying
            refreshActiveThreadMessages(activeThread.id);
         }, 800); // 800ms allows slow Spring Boot transactions to persist
      }
    };

    window.addEventListener('app:new_notification', handleNewNotification);
    return () => window.removeEventListener('app:new_notification', handleNewNotification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.id]); // Rebind if activeThread changes so we read correct ID

  const fetchThreads = async () => {
    try {
      const data = await getMyThreads(userRole?.toUpperCase());
      setThreads(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load conversations.');
      setLoading(false);
    }
  };

  const handleThreadSelect = async (thread) => {
    setActiveThread(thread);
    setMobileView('chat');

    // Push new state to history to enable hardware back button on mobile
    if (window.innerWidth <= 768) {
      window.history.pushState({ view: 'chat' }, '');
    }

    try {
      const res = await getThreadMessages(thread.id, 0, 30);
      const rawMessages = Array.isArray(res) ? res : (res?.content || res?.data || []);
      const messages = [...rawMessages].reverse(); // Latest at bottom
      setActiveThread(prev => prev?.id === thread.id ? { ...prev, messages } : prev);

      // Mark as read after opening
      await markThreadAsRead(thread.id);
      setThreads(prev => prev.map(t =>
        t.id === thread.id ? { ...t, unreadCount: 0, unread: 0, lastReadAt: new Date().toISOString() } : t
      ));
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const openThreadById = async (targetThreadId, threadData = null) => {
    const targetKey = String(targetThreadId);
    const targetThread =
      threads.find((t) => String(t.id) === targetKey) ||
      (threadData && String(threadData.id) === targetKey ? threadData : null);

    if (targetThread) {
      if (!threads.find((t) => String(t.id) === targetKey)) {
        setThreads((prev) => [targetThread, ...(prev || [])]);
      }
      await handleThreadSelect(targetThread);
      return;
    }

    setActiveThread({
      id: targetKey,
      displayName: threadData?.displayName || 'Loading...',
      displaySubtitle: threadData?.displaySubtitle || threadData?.propertyName || '',
      displayLabel: threadData?.displayLabel || getThreadDisplayLabel(threadData),
      chatWithKind: threadData?.chatWithKind,
      loading: true,
    });
    setMobileView('chat');
    await refreshActiveThreadMessages(targetKey);
  };

  // Handle direct navigation to a thread (e.g. from unit requests or notifications)
  useEffect(() => {
    const threadIdFromLocation = location.state?.threadId;
    const threadDataFromLocation = threadData || location.state?.threadData;
    const targetThreadId = threadId || threadIdFromLocation;

    if (!targetThreadId) {
      appliedNavThreadIdRef.current = null;
      return;
    }

    const targetKey = String(targetThreadId);

    // Only auto-open when navigation intent changes — don't override manual sidebar picks
    if (appliedNavThreadIdRef.current === targetKey) {
      if (activeThread?.loading && String(activeThread.id) === targetKey) {
        const fullThreadData =
          threads.find((t) => String(t.id) === targetKey) ||
          (threadDataFromLocation && String(threadDataFromLocation.id) === targetKey
            ? threadDataFromLocation
            : null);
        if (fullThreadData) {
          setActiveThread((prev) => ({
            ...fullThreadData,
            messages: prev?.messages || [],
            loading: false,
          }));
        }
      }
      return;
    }

    appliedNavThreadIdRef.current = targetKey;
    openThreadById(targetKey, threadDataFromLocation);

    if (threadIdFromLocation) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, threadData, location.state?.threadId, location.state?.threadData, threads]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const tempId = Date.now();
    const newMessage = {
      id: tempId,
      text: messageInput,
      sender: 'me',
      isMine: true,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date().toISOString(),
      isOptimistic: true
    };

    // Optimistic UI update for the active chat window
    setActiveThread(prev => ({
      ...prev,
      messages: [...(prev.messages || []), newMessage]
    }));

    // Optimistic UI update for the sidebar threads list
    setThreads(prev => (prev || []).map(t => 
      t.id === activeThread.id
        ? { ...t, displayMsg: newMessage.text, displayTime: newMessage.time }
        : t
    ));

    const textToSend = messageInput;
    setMessageInput('');

    try {
      const recipientUserId =
        activeThread.recipientUserId ||
        activeThread.intendedRecipientUserId ||
        activeThread.chatWithUserId ||
        activeThread.guestUserId ||
        activeThread.tenantUserId ||
        null;
      const realMessage = await sendMessage(activeThread.id, {
        text: textToSend,
        recipientUserId,
        receiverUserId: recipientUserId,
        targetUserId: recipientUserId,
        chatWithUserId: recipientUserId,
        tenantUserId: recipientUserId || activeThread.tenantUserId,
        guestUserId: recipientUserId || activeThread.guestUserId,
        propertyId: activeThread.propertyId,
        chatMode: activeThread.chatMode,
      });
      // Replace optimistic message with real message from server
      setActiveThread(prev => {
        const currentMessages = prev.messages || [];
        const hasTemp = currentMessages.some(m => String(m.id) === String(tempId));
        let updatedMessages;

        if (hasTemp) {
          updatedMessages = currentMessages.map(m => 
            String(m.id) === String(tempId) ? { ...realMessage, isMine: true, isOptimistic: true } : m
          );
        } else {
          // If wiped by refreshActiveThreadMessages, restore it!
          const hasReal = currentMessages.some(m => String(m.id) === String(realMessage.id));
          if (hasReal) {
             updatedMessages = currentMessages;
          } else {
             updatedMessages = [...currentMessages, { ...realMessage, isMine: true, isOptimistic: true }];
          }
        }
        return { ...prev, messages: updatedMessages };
      });
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleStartSupportChat = async () => {
    try {
      const normalizedRole = userRole?.toUpperCase();
      let thread;

      if (normalizedRole === 'TENANT') {
        thread = await getOrCreateTenantAdminThread();
      } else if (normalizedRole === 'LANDLORD' || normalizedRole === 'AGENT') {
        thread = await getOrCreateLandlordAdminThread();
      } else if (normalizedRole === 'BNB_HOST' || normalizedRole === 'HOST') {
        thread = await getOrCreateBnbHostAdminThread();
      } else {
        // Fallback for any other logged-in role
        thread = await getOrCreateTenantAdminThread();
      }

      // Ensure it's in our threads list
      if (!threads.find(t => t.id === thread.id)) {
        setThreads(prev => [thread, ...prev]);
      }
      handleThreadSelect(thread);
    } catch (err) {
      console.error('Failed to start support chat:', err);
    }
  };



  const filteredThreads = Array.isArray(threads)
    ? threads.filter(t =>
      (t.displayName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (t.displayLabel?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (t.displaySubtitle?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (t.propertyName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (t.property?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    )
    : [];

  return (
    <div className={`messages-container mobile-${mobileView}`}>
      {/* Sidebar - Thread List */}
      <div className={`messages-sidebar ${mobileView === 'chat' ? 'mobile-hidden' : ''}`}>
        <CollapsibleTopPanel id="messages-sidebar-top-panel" defaultOpenOnMobile={false}>
          <div className="messages-sidebar-header">
            <div className="sidebar-header-top">
              <h2>Messages</h2>
              {userRole?.toUpperCase() !== 'SYSTEM_ADMIN' && userRole?.toUpperCase() !== 'ADMIN' && (
                <button className="support-btn" onClick={handleStartSupportChat} title="Contact Admin Support">
                  Support
                </button>
              )}
            </div>
            <div className="search-box">
              <span className="search-icon"><IoSearch size={18} /></span>
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CollapsibleTopPanel>
        <div className="thread-list">
          {loading ? (
            <div className="messages-loading">Loading chats...</div>
          ) : error ? (
            <div className="messages-error">{error}</div>
          ) : filteredThreads.length === 0 ? (
            <div className="messages-empty-state">No conversations found.</div>
          ) : (
            filteredThreads.map(thread => (
              <div
                key={thread.id}
                className={`thread-item ${activeThread?.id === thread.id ? 'active' : ''}`}
                onClick={() => handleThreadSelect(thread)}
              >
                <div className="thread-avatar">
                  {thread.displayName?.charAt(0) || '?'}
                  {thread.online && <div className="status-indicator"></div>}
                </div>
                <div className="thread-info">
                  <div className="thread-name">
                    <span className="thread-name-label">{getThreadDisplayLabel(thread)}</span>
                    <span className="thread-time">{thread.displayTime}</span>
                  </div>
                  <div className="thread-last-msg">{thread.displayMsg || 'No messages yet'}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Window */}
      {activeThread ? (
        <div className={`chat-window ${mobileView === 'list' ? 'mobile-hidden' : ''}`}>
          <div className="chat-header">
            <div className="chat-user-info">

              <div className="thread-avatar" style={{
                width: '40px',
                height: '40px',
                fontSize: '0.9rem',
                marginRight: '12px',
                background: '#22c55e' /* Project Green for others */
              }}>
                {activeThread.displayName?.charAt(0) || '?'}
              </div>
              <div className="chat-user-details">
                <h3 className="chat-thread-title">{getThreadDisplayLabel(activeThread)}</h3>
                {activeThread.chatWithKind && (
                  <span className="chat-user-kind">
                    {activeThread.chatWithKind.charAt(0).toUpperCase() + activeThread.chatWithKind.slice(1).toLowerCase().replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="messages-area">
            {!Array.isArray(activeThread.messages) ? (
              <div className="messages-loading">Loading messages...</div>
            ) : activeThread.messages.length === 0 ? (
              <div className="messages-empty-state">No messages yet. Start the conversation!</div>
            ) : (
              activeThread.messages.map(msg => (
                <div key={msg.id} className={`message-bubble ${msg.sender === 'me' || msg.isMine || String(msg.senderId) === String(currentUserId) ? 'me' : 'other'}`}>
                  <div className="message-text">{msg.text}</div>
                  <div className="message-time">{msg.time || new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-container" onSubmit={handleSendMessage}>
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="Type your message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
              />
              <button type="submit" className="send-btn" disabled={!messageInput.trim()}>
                <IoSend className="send-icon" size={18} />
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className={`messages-empty ${mobileView === 'list' ? 'mobile-hidden' : ''}`}>
          <div className="empty-icon"><IoChatbubbleEllipsesOutline size={48} /></div>
          <h3>Select a conversation</h3>
          <p>Choose a thread from the left to start messaging.</p>
        </div>
      )}
    </div>
  );
};

export default Messages;
